import { useSnackbar } from 'notistack'
import React, { FC, useEffect, useRef, useState } from 'react'
import { CostumerPropsType, CostumerType, FloorMaterialPropsType, FloorMaterialType, MaterialType } from '../../../api/appAPI'
import FloorMaterialUIBox from './FloorMaterialUIBox'
import { EnqueueSnackbarType, showMessage } from '../../../components/common/Helpers'
import { AvailableMaterialsForFM } from '../../../redux/itemReducer'

interface IProps {
    materials: AvailableMaterialsForFM
    fm: FloorMaterialType

    editFloorMaterial(id: number, fmProps: FloorMaterialPropsType, enqueueSnackbar: EnqueueSnackbarType): void
}

const EditFloorMaterial: FC<IProps> = ({ fm, editFloorMaterial, ...props }) => {
    const idRef = useRef(fm.id)
    const [fmProps, setFmProps] = useState({
        name: fm.name,
        sku: fm.sku,
        materialId: fm.materialId,
        dealerId: fm.dealerId,
        floorSetupCharge: fm.floorSetupCharge.toString(),
        floorPricePerSqFt: fm.floorPricePerSqFt.toString(),
        curbSetupCharge: fm.curbSetupCharge.toString(),
        curbPricePerSqFt: fm.curbPricePerSqFt.toString(),
        curbLinearPricePerFt: fm.curbLinearPricePerFt.toString(),
        shortDescription: fm.shortDescription,
        longDescription: fm.longDescription,
        pictureUrl: fm.pictureUrl,
        pdfUrl: fm.pdfUrl
    })
    const fmPropsRef = useRef(fmProps)

    const [needSave, setNeedSave] = useState(false)
    const needSaveRef = useRef(needSave)

    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        idRef.current = fm.id
        setFmProps({
            name: fm.name,
            sku: fm.sku,
            materialId: fm.materialId,
            dealerId: fm.dealerId,
            floorSetupCharge: fm.floorSetupCharge.toString(),
            floorPricePerSqFt: fm.floorPricePerSqFt.toString(),
            curbSetupCharge: fm.curbSetupCharge.toString(),
            curbPricePerSqFt: fm.curbPricePerSqFt.toString(),
            curbLinearPricePerFt: fm.curbLinearPricePerFt.toString(),
            shortDescription: fm.shortDescription,
            longDescription: fm.longDescription,
            pictureUrl: fm.pictureUrl,
            pdfUrl: fm.pdfUrl
        })

        setNeedSave(false)

    }, [fm])

    useEffect(() => {
        fmPropsRef.current = fmProps
        needSaveRef.current = needSave
    }, [needSave, fmProps])

    useEffect(() => () => {
        if (needSaveRef.current) {
            if (window?.confirm('Save floor material?')) {
                handleSaveClick()
            }
        }
    }, [fm])

    const handleNameChange = (event: React.ChangeEvent<{ value: string }>) => {
        setFmProps({
            ...fmProps,
            name: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }
    const handleSkuChange = (event: React.ChangeEvent<{ value: string }>) => {
        setFmProps({
            ...fmProps,
            sku: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }
    const handleMaterialIdChange = (value: number) => {
        setFmProps({
            ...fmProps,
            materialId: value
        })
        setNeedSave(true)
    }
    const handlePictureUrlChange = (event: React.ChangeEvent<{ value: string }>) => {
        setFmProps({
            ...fmProps,
            pictureUrl: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }
    const handlePdfUrlChange = (event: React.ChangeEvent<{ value: string }>) => {
        setFmProps({
            ...fmProps,
            pdfUrl: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }
    const handleShortDescriptionChange = (event: React.ChangeEvent<{ value: string }>) => {
        setFmProps({
            ...fmProps,
            shortDescription: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }
    const handleLongDescriptionChange = (event: React.ChangeEvent<{ value: string }>) => {
        setFmProps({
            ...fmProps,
            longDescription: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }
    const handleFloorSetupChargeChange = (event: React.ChangeEvent<{ value: string }>) => {
        setFmProps({
            ...fmProps,
            floorSetupCharge: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }
    const handleFloorPricePerSqFtChange = (event: React.ChangeEvent<{ value: string }>) => {
        setFmProps({
            ...fmProps,
            floorPricePerSqFt: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }
    const handleCurbSetupChargeChange = (event: React.ChangeEvent<{ value: string }>) => {
        setFmProps({
            ...fmProps,
            curbSetupCharge: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }
    const handleCurbPricePerSqFtChange = (event: React.ChangeEvent<{ value: string }>) => {
        setFmProps({
            ...fmProps,
            curbPricePerSqFt: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }
    const handleCurbLinearPricePerFtChange = (event: React.ChangeEvent<{ value: string }>) => {
        setFmProps({
            ...fmProps,
            curbLinearPricePerFt: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }

    const handleSaveClick = () => {
        const name = fmPropsRef.current.name
        const sku = fmPropsRef.current.sku
        const materialId = fmPropsRef.current.materialId
        const dealerId = fmPropsRef.current.dealerId
        const floorSetupCharge = fmPropsRef.current.floorSetupCharge
        const floorPricePerSqFt = fmPropsRef.current.floorPricePerSqFt
        const curbSetupCharge = fmPropsRef.current.curbSetupCharge
        const curbPricePerSqFt = fmPropsRef.current.curbPricePerSqFt
        const curbLinearPricePerFt = fmPropsRef.current.curbLinearPricePerFt
        const shortDescription = fmPropsRef.current.shortDescription
        const longDescription = fmPropsRef.current.longDescription
        const pictureUrl = fmPropsRef.current.pictureUrl
        const pdfUrl = fmPropsRef.current.pdfUrl

        if (name === '') {
            showMessage(enqueueSnackbar, "Enter name")
            return
        }

        if (sku === '') {
            showMessage(enqueueSnackbar, "Enter sku")
            return
        }

        needSaveRef.current = false
        setNeedSave(false)

        const newFMProps = {
            name, sku, materialId,
            dealerId, pictureUrl, pdfUrl, shortDescription, longDescription,
            floorSetupCharge: parseFloat(floorSetupCharge),
            floorPricePerSqFt: parseFloat(floorPricePerSqFt),
            curbSetupCharge: parseFloat(curbSetupCharge),
            curbPricePerSqFt: parseFloat(curbPricePerSqFt),
            curbLinearPricePerFt: parseFloat(curbLinearPricePerFt)
        } as FloorMaterialPropsType

        editFloorMaterial(idRef.current, newFMProps, enqueueSnackbar)
    }

    return (
        <FloorMaterialUIBox

            materials={props.materials}

            title='Edit Floor Material'

            name={{
                value: fmProps.name,
                onChange: handleNameChange
            }}
            sku={{
                value: fmProps.sku,
                onChange: handleSkuChange
            }}
            materialId={{
                value: fmProps.materialId,
                onChange: handleMaterialIdChange
            }}
            floorSetupCharge={{
                value: fmProps.floorSetupCharge,
                onChange: handleFloorSetupChargeChange
            }}
            floorPricePerSqFt={{
                value: fmProps.floorPricePerSqFt,
                onChange: handleFloorPricePerSqFtChange
            }}
            curbSetupCharge={{
                value: fmProps.curbSetupCharge,
                onChange: handleCurbSetupChargeChange
            }}
            curbPricePerSqFt={{
                value: fmProps.curbPricePerSqFt,
                onChange: handleCurbPricePerSqFtChange
            }}
            curbLinearPricePerFt={{
                value: fmProps.curbLinearPricePerFt,
                onChange: handleCurbLinearPricePerFtChange
            }}
            shortDescription={{
                value: fmProps.shortDescription,
                onChange: handleShortDescriptionChange
            }}
            longDescription={{
                value: fmProps.longDescription,
                onChange: handleLongDescriptionChange
            }}
            pictureUrl={{
                value: fmProps.pictureUrl,
                onChange: handlePictureUrlChange
            }}
            pdfUrl={{
                value: fmProps.pdfUrl,
                onChange: handlePdfUrlChange
            }}

            button={{
                text: 'Save',
                onClick: handleSaveClick
            }}

            needSave={needSave}
        />
    )
}

export default EditFloorMaterial